import { CameraControls, PerspectiveCamera } from '@react-three/drei';
import { Canvas } from '@react-three/fiber';
import { useEffect, useRef, useState } from 'react';
import { useSnapshot } from 'valtio';

import {
  applyShowSkeleton,
  disableFrustumCulled,
  hideMeshMaterials,
  turnOnShadows,
} from '@cartwhl/3d/gltf/utils';
import Lights from '@cartwhl/3d/r3f/components/Lights';
import useGLTFWithCredentials from '@cartwhl/3d/r3f/hooks/useGLTFWithCredentials';
import putCharacter from '@cartwhl/common/api/putCharacter';
import { StudioModalName } from '@cartwhl/common/stores/navigationStore';
import {
  TRACKED_FEATURES,
  trackFeatureAbandoned,
  trackFeatureCompleted,
  trackFeatureStepCompleted,
} from '@cartwhl/tracking/featureTracking';
import handleGenericErrors from '@cartwhl/common/utils/handleGenericErrors';
import Select from '@cartwhl/ui/atoms/Select';
import Slider from '@cartwhl/ui/atoms/Slider';
import Text from '@cartwhl/ui/base/Text';
import PauseIcon from '@cartwhl/ui/icons/PauseIcon';
import PlayIcon from '@cartwhl/ui/icons/PlayIcon';
import RadioFilledIcon from '@cartwhl/ui/icons/RadioFilledIcon';
import RadioUnfilledIcon from '@cartwhl/ui/icons/RadioUnfilledIcon';
import Button from '@cartwhl/ui/input/Button';
import GoButton from '@cartwhl/ui/input/GoButton';
import Modal from '@cartwhl/ui/molecules/Modal';

import {
  characterPickerStore,
  navigationStore,
  noticesStore,
  projectStore,
} from 'stores';

import {
  CAMERA_POSITION,
  DEMO_ANIMATION_PATH_1,
  DEMO_ANIMATION_PATH_2,
  DEMO_ANIMATION_PATH_3,
  MODAL_MAX_WIDTH,
  ROTATION_LIMITS,
} from '../constants';
import { createRotationOffsets, getRotationOffset } from '../utils';
import CharacterVerificationStage from './CharacterVerificationStage';
import LoadingCharacter from './LoadingCharacter';
import ModalLayout from './ModalLayout';
import SetInitalCameraPosition from './SetInitialCameraPosition';
import VerificationCameraButtons, {
  VerificationCameraButtonsSlot,
} from './VerificationCameraButtons';

export default function UploadVerificationModal({
  close,
  payload: { characterID },
}: {
  close: () => void;
  payload: {
    characterID: string;
  };
}) {
  const { characters } = useSnapshot(characterPickerStore.state);
  const activeCharacter = characters?.find((c) => c.jobID === characterID);

  const [submitting, setSubmitting] = useState(false);
  const availabilityTrackedRef = useRef(false);

  const [step, setStep] = useState<0 | 1>(0);
  const [selectedProcess, setSelectedProcess] = useState<
    'retargeting' | 'auto-rigging' | ''
  >('');
  const [isPlaying, setIsPlaying] = useState(true);
  const [selectedAnimation, setSelectedAnimation] = useState(
    DEMO_ANIMATION_PATH_2,
  );
  const isGeneration = characterPickerStore.isGenerationJob(characterID);
  const modalTitle = isGeneration
    ? 'Character generation verification'
    : 'Upload verification';

  // Rotation adjustment states
  const [headRotation, setHeadRotation] = useState(0);
  const [collarRotation, setCollarRotation] = useState(0);
  const [shoulderRotation, setShoulderRotation] = useState(0);
  const [hipRotation, setHipRotation] = useState(0);
  const [ankleRotation, setAnkleRotation] = useState(0);

  const meshData = useGLTFWithCredentials(
    activeCharacter?.meshPath,
    undefined,
    [hideMeshMaterials, turnOnShadows, disableFrustumCulled],
  );

  const allowAutoRigging = activeCharacter?.markerAdjustmentEligible;
  const allowRetargeting = activeCharacter?.mappingAdjustmentEligible;
  const noOptions = !allowAutoRigging && !allowRetargeting;

  useEffect(() => {
    if (!activeCharacter || availabilityTrackedRef.current) return;

    trackFeatureStepCompleted(
      TRACKED_FEATURES.CHARACTER_UPLOAD,
      'verification_options_loaded',
      {
        character_id: characterID,
        metadata: {
          rigging_available: Boolean(allowAutoRigging),
          retargeting_available: Boolean(allowRetargeting),
          no_options: Boolean(noOptions),
          is_generation: Boolean(isGeneration),
        },
      },
    );
    availabilityTrackedRef.current = true;
  }, [
    activeCharacter,
    allowAutoRigging,
    allowRetargeting,
    noOptions,
    isGeneration,
    characterID,
  ]);

  useEffect(() => {
    if (meshData) {
      applyShowSkeleton(meshData, false);
      if (!allowAutoRigging) {
        setSelectedProcess('retargeting');
      } else if (!allowRetargeting) {
        setSelectedProcess('auto-rigging');
      }
    }
  }, [meshData]);

  // Set initial rotation values when character loads
  useEffect(() => {
    if (activeCharacter) {
      const headValue = getRotationOffset(activeCharacter, 'head');
      const collarValue = getRotationOffset(activeCharacter, 'collar');
      const shoulderValue = getRotationOffset(activeCharacter, 'shoulder');
      const hipValue = getRotationOffset(activeCharacter, 'hip');
      const ankleValue = getRotationOffset(activeCharacter, 'ankle');

      // Set current values
      setHeadRotation(headValue);
      setCollarRotation(collarValue);
      setShoulderRotation(shoulderValue);
      setHipRotation(hipValue);
      setAnkleRotation(ankleValue);
    }
  }, [activeCharacter]);

  if (noOptions && step === 1) {
    const isGeneration = characterPickerStore.isGenerationJob(characterID);
    noticesStore.error(
      `This is an old character and has no options available. Please try ${isGeneration ? 'generating again' : 'uploading again'} or reach out for support.`,
    );
  }

  async function handleSubmitRotationOffsets() {
    if (!activeCharacter) {
      noticesStore.error('Character not found');
      return;
    }
    if (activeCharacter.createdBy.includes('FREE')) {
      noticesStore.error('This feature is only available for your characters');
      return;
    }

    const prev = activeCharacter.rotationOffsets;
    if (
      prev?.head?.[0] === headRotation &&
      prev?.left_collar?.[2] === collarRotation &&
      prev?.left_shoulder?.[2] === shoulderRotation &&
      prev?.left_hip?.[2] === hipRotation &&
      prev?.left_ankle?.[1] === ankleRotation
    ) {
      // No changes to the rotation offsets, so don't submit
      return;
    }

    const rotationOffsets = createRotationOffsets(
      headRotation,
      collarRotation,
      hipRotation,
      shoulderRotation,
      ankleRotation,
    );

    const { error } = await putCharacter(activeCharacter.jobID, {
      rotationOffsets,
    });

    if (error) {
      await handleGenericErrors(error);
    } else {
      noticesStore.success('Character rotation updated successfully!');

      // Now refresh character data
      characterPickerStore.fetchAvailableCharacters();
    }
  }

  return (
    <Modal
      open
      title={modalTitle}
      onOpenChange={close}
      maxWidth={MODAL_MAX_WIDTH}
      fullscreenOnMobile
      fixedHeightContent
      closeOnClickOutside={false}
    >
      <ModalLayout.Root>
        <ModalLayout.Stage>
          <ModalLayout.Sidebar>
            {step === 0 && (
              <>
                <Text as="h2" variant="studio/body/medium/md" className="mb-2">
                  Review character animations
                </Text>
                <p className="text-gray-60 dark:text-gray-45 text-[14px]/[16px]">
                  Watch the animation and verify that your character has been
                  properly rigged. Adjust the default joint rotations if
                  necessary.
                </p>

                <div className="mt-4">
                  <Text
                    as="h2"
                    variant="studio/body/medium/sm"
                    className="mb-2"
                  >
                    Select animation to review:
                  </Text>
                  <Select
                    inModal={true}
                    items={[
                      { value: DEMO_ANIMATION_PATH_1, label: 'Aerobics' },
                      { value: DEMO_ANIMATION_PATH_2, label: 'Bouncy walk' },
                      { value: DEMO_ANIMATION_PATH_3, label: 'T-pose' },
                    ]}
                    value={selectedAnimation}
                    onValueChange={setSelectedAnimation}
                    placeholder="Select an animation"
                  />
                </div>

                {activeCharacter &&
                  !activeCharacter.createdBy.includes('FREE') && (
                    <div className="mt-6 min-h-[300px] flex-1">
                      <div className="bg-gray-15 border-gray-30 dark:bg-gray-75 dark:border-gray-60 flex h-full min-h-0 flex-col rounded-lg border">
                        <Text
                          as="h3"
                          variant="studio/body/medium/sm"
                          className="border-gray-30 dark:border-gray-60 border-b px-2 py-3"
                        >
                          Adjust joint rotations
                        </Text>

                        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-scroll p-2">
                          <JointRotationSlider
                            label="Head rotation"
                            currentValue={headRotation}
                            onChange={setHeadRotation}
                            min={ROTATION_LIMITS.X_HEAD.MIN}
                            max={ROTATION_LIMITS.X_HEAD.MAX}
                          />

                          <JointRotationSlider
                            label="Shoulder rotation"
                            currentValue={collarRotation}
                            onChange={setCollarRotation}
                            min={ROTATION_LIMITS.Z_COLLAR.MIN}
                            max={ROTATION_LIMITS.Z_COLLAR.MAX}
                          />

                          <JointRotationSlider
                            label="Arm rotation"
                            currentValue={shoulderRotation}
                            onChange={setShoulderRotation}
                            min={ROTATION_LIMITS.Z_SHOULDER.MIN}
                            max={ROTATION_LIMITS.Z_SHOULDER.MAX}
                          />

                          <JointRotationSlider
                            label="Hip rotation"
                            currentValue={hipRotation}
                            onChange={setHipRotation}
                            min={ROTATION_LIMITS.Z_HIP.MIN}
                            max={ROTATION_LIMITS.Z_HIP.MAX}
                          />

                          <JointRotationSlider
                            label="Foot rotation"
                            currentValue={ankleRotation}
                            onChange={setAnkleRotation}
                            min={ROTATION_LIMITS.Y_ANKLE.MIN}
                            max={ROTATION_LIMITS.Y_ANKLE.MAX}
                          />
                        </div>
                      </div>
                    </div>
                  )}
              </>
            )}

            {step === 1 && (
              <div className="flex flex-col gap-2">
                <Text as="h2" variant="studio/body/medium/md" className="mb-2">
                  Why won't my character upload properly?
                </Text>

                <p className="text-gray-60 dark:text-gray-45 text-[14px]/[16px]">
                  If the animation isn't appearing correctly on your uploaded
                  character, there may be something we don't understand with its
                  geometry, proportions or rig.
                  <br />
                  <br />
                  In order to get your character set up properly, you may need
                  to use our auto-rigger to manually place markers, or set up
                  your character for animation retargeting.
                  <br />
                  <br />
                  To continue trying to resolve the issue, please select an
                  option below and then hit{' '}
                  <b className="text-gray-90 dark:text-white">"Next"</b> to
                  continue:
                </p>

                <Text
                  as="h3"
                  variant="studio/body/medium/md"
                  className="mt-4 mb-2"
                >
                  Select an option:
                </Text>
                <button
                  className="border-gray-30 dark:border-gray-75 dark:hover:bg-gray-75 hover:bg-gray-15 flex h-10 items-center gap-4 rounded-lg border pr-4 pl-2 text-[14px]/[18px]"
                  onClick={() => setSelectedProcess('auto-rigging')}
                  style={{ opacity: allowAutoRigging ? 1 : 0.25 }}
                  disabled={!allowAutoRigging}
                >
                  <AutoRiggingIcon className="flex-none" />
                  Auto-rig w/ markers
                  {selectedProcess === 'auto-rigging' ? (
                    <RadioFilledIcon className="text-blue ml-auto flex-none" />
                  ) : (
                    <RadioUnfilledIcon className="ml-auto flex-none" />
                  )}
                </button>
                <button
                  className="border-gray-30 dark:border-gray-75 dark:hover:bg-gray-75 hover:bg-gray-15 flex h-10 items-center gap-4 rounded-lg border pr-4 pl-2 text-[14px]/[18px]"
                  onClick={() => setSelectedProcess('retargeting')}
                  style={{ opacity: allowRetargeting ? 1 : 0.25 }}
                  disabled={!allowRetargeting}
                >
                  <RetargetIcon className="flex-none" />
                  Set up Retargeting
                  {selectedProcess === 'retargeting' ? (
                    <RadioFilledIcon className="text-blue ml-auto flex-none" />
                  ) : (
                    <RadioUnfilledIcon className="ml-auto flex-none" />
                  )}
                </button>
              </div>
            )}
          </ModalLayout.Sidebar>
          <ModalLayout.Main>
            {meshData ? (
              <>
                <Canvas>
                  <Lights />
                  <CameraControls makeDefault />
                  <PerspectiveCamera makeDefault position={CAMERA_POSITION} />
                  <SetInitalCameraPosition />

                  <CharacterVerificationStage
                    rotationOffsets={createRotationOffsets(
                      headRotation,
                      collarRotation,
                      hipRotation,
                      shoulderRotation,
                      ankleRotation,
                    )}
                    meshData={meshData}
                    isPlaying={isPlaying}
                    animationPath={selectedAnimation}
                  />
                  <VerificationCameraButtons />
                </Canvas>
                <div className="absolute top-4 right-4 z-10">
                  <GoButton
                    icon={isPlaying ? <PauseIcon /> : <PlayIcon />}
                    onClick={() => setIsPlaying(!isPlaying)}
                  />
                </div>
                <VerificationCameraButtonsSlot.Out />
              </>
            ) : (
              <LoadingCharacter />
            )}
          </ModalLayout.Main>
        </ModalLayout.Stage>

        <ModalLayout.Buttons>
          <Button
            variant="secondary"
            onClick={() => {
              trackFeatureAbandoned(TRACKED_FEATURES.CHARACTER_UPLOAD, {
                character_id: characterID,
                step: `verification_step_${step}`,
              });
              close();
            }}
          >
            Cancel
          </Button>
          <div className="flex gap-2">
            {step === 0 ? (
              <>
                <Button
                  variant="secondary"
                  disabled={submitting}
                  onClick={() => {
                    trackFeatureStepCompleted(
                      TRACKED_FEATURES.CHARACTER_UPLOAD,
                      'needs_fix',
                      { character_id: characterID },
                    );
                    setStep(1);
                  }}
                >
                  Doesn't look right
                </Button>
                <Button
                  variant="primary"
                  loading={submitting}
                  onClick={async () => {
                    if (!activeCharacter) return;
                    setSubmitting(true);
                    await handleSubmitRotationOffsets();
                    await projectStore.changeCurrentCharacter(
                      activeCharacter.jobID,
                    );
                    trackFeatureCompleted(TRACKED_FEATURES.CHARACTER_UPLOAD, {
                      character_id: characterID,
                    });
                    setSubmitting(false);
                    close();
                  }}
                >
                  Looks good
                </Button>
              </>
            ) : (
              <>
                <Button variant="secondary" onClick={() => setStep(0)}>
                  Back
                </Button>
                <Button
                  variant="primary"
                  onClick={() => {
                    if (selectedProcess === 'retargeting') {
                      trackFeatureStepCompleted(
                        TRACKED_FEATURES.CHARACTER_UPLOAD,
                        'resolution_path_selected',
                        {
                          character_id: characterID,
                          metadata: {
                            selected_path: 'retargeting',
                            rigging_available: Boolean(allowAutoRigging),
                            retargeting_available: Boolean(allowRetargeting),
                          },
                        },
                      );
                      navigationStore.setModal(StudioModalName.RETARGETING, {
                        characterID,
                      });
                    } else if (selectedProcess === 'auto-rigging') {
                      trackFeatureStepCompleted(
                        TRACKED_FEATURES.CHARACTER_UPLOAD,
                        'resolution_path_selected',
                        {
                          character_id: characterID,
                          metadata: {
                            selected_path: 'auto-rigging',
                            rigging_available: Boolean(allowAutoRigging),
                            retargeting_available: Boolean(allowRetargeting),
                          },
                        },
                      );
                      navigationStore.setModal(StudioModalName.AUTO_RIGGING, {
                        characterID,
                      });
                    }
                  }}
                >
                  Next
                </Button>
              </>
            )}
          </div>
        </ModalLayout.Buttons>
      </ModalLayout.Root>
    </Modal>
  );
}

const AutoRiggingIcon = ({ className }: { className?: string }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path d="M9.99365 6.6084C9.24463 6.6084 8.63525 5.99902 8.63525 5.24365C8.63525 4.48828 9.24463 3.87891 9.99365 3.87891C10.749 3.87891 11.3584 4.48828 11.3584 5.24365C11.3584 5.99902 10.749 6.6084 9.99365 6.6084ZM9.99365 12.3594C9.76514 12.3594 9.67627 12.5054 9.53662 12.9434C9.32715 13.5654 8.71143 16.1235 8.50195 16.8726C8.39404 17.2661 8.19092 17.4312 7.88623 17.4312C7.47998 17.4312 7.22607 17.0566 7.34033 16.6567C7.38477 16.4917 8.26709 12.791 8.39404 12.0293C8.51465 11.2485 8.53369 9.54736 8.521 9.02051C8.51465 8.67139 8.34961 8.43652 8.06396 8.38574C7.77197 8.32861 5.46143 8.03662 5.125 7.9668C4.83936 7.90332 4.66162 7.65576 4.66162 7.36377C4.66162 6.99561 4.92822 6.76074 5.21387 6.76074C5.30908 6.76074 5.4043 6.78613 5.51855 6.81152C6.47705 6.99561 8.54639 7.20508 9.99365 7.20508C11.46 7.20508 13.5166 6.98291 14.4751 6.81152C14.583 6.78613 14.6782 6.76074 14.7861 6.76074C15.0591 6.76074 15.3384 7.00195 15.3384 7.36377C15.3384 7.64941 15.1479 7.87158 14.875 7.96045C14.5513 8.06836 12.2153 8.31592 11.9297 8.38574C11.644 8.44922 11.4854 8.67773 11.4854 9.02051C11.479 9.54736 11.479 11.2485 11.5996 12.0293C11.7139 12.791 12.6089 16.4917 12.6533 16.6567C12.7612 17.063 12.5137 17.4312 12.1074 17.4312C11.7964 17.4312 11.5806 17.2598 11.4917 16.8726C11.314 16.1172 10.6665 13.5527 10.4697 12.9434C10.3364 12.499 10.2349 12.3594 9.99365 12.3594Z" />
  </svg>
);

const JointRotationSlider = ({
  label,
  currentValue,
  onChange,
  min,
  max,
}: {
  label: string;
  currentValue: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
}) => (
  <div className="flex flex-col gap-2">
    <Text variant="studio/body/sm" className="text-gray-60 dark:text-gray-45">
      {label}
    </Text>
    <Slider
      currentValue={currentValue}
      onChange={onChange}
      min={min}
      max={max}
      step={1}
      rangeColor="blue"
    />
    <div className="flex justify-between">
      <Text variant="studio/body/sm" className="text-gray-60 dark:text-gray-45">
        {min}°
      </Text>
      <Text variant="studio/body/sm" className="text-gray-60 dark:text-gray-45">
        {currentValue}°
      </Text>
      <Text variant="studio/body/sm" className="text-gray-60 dark:text-gray-45">
        {max}°
      </Text>
    </div>
  </div>
);

const RetargetIcon = ({ className }: { className?: string }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <g clipPath="url(#clip0_4317_112565)">
      <path d="M8.7511 17.967C4.26014 17.967 0.554688 14.2616 0.554688 9.77063C0.554688 5.2876 4.26806 1.57422 8.75904 1.57422C12.9247 1.57422 16.4158 4.77979 16.884 8.82642C16.4 8.71533 15.7573 8.68359 15.2416 8.77088C14.7735 5.59704 12.0677 3.19288 8.75904 3.19288C5.1012 3.19288 2.17334 6.12073 2.16541 9.77063C2.15747 13.4205 5.1012 16.3563 8.7511 16.3563C9.56042 16.3563 10.3221 16.2055 11.0283 15.9438C11.2346 16.4515 11.5203 16.9276 11.8852 17.3323C10.9172 17.7449 9.86194 17.967 8.7511 17.967ZM8.75904 6.6206C8.19567 6.6206 7.74341 6.1604 7.74341 5.60498C7.74341 5.04956 8.19567 4.59729 8.75904 4.59729C9.32239 4.59729 9.77466 5.04956 9.77466 5.60498C9.77466 6.1604 9.32239 6.6206 8.75904 6.6206ZM8.75904 7.13635C9.86194 7.13635 11.4409 6.95385 12.1947 6.83484C12.2661 6.8269 12.3375 6.8031 12.4089 6.8031C12.6073 6.8031 12.8057 6.96973 12.8057 7.24744C12.8057 7.46167 12.6787 7.63623 12.4724 7.69178C12.2264 7.76319 10.4491 7.96948 10.2269 8.00915C10.0048 8.05677 9.86987 8.23925 9.86194 8.50903C9.854 8.9137 9.86194 10.215 9.96509 10.8021C10.0603 11.3893 10.7347 14.214 10.7665 14.333C10.8538 14.6345 10.6713 14.9043 10.3618 14.9043C10.1476 14.9043 9.99682 14.7853 9.91749 14.4916C9.77466 13.9204 9.29065 11.9685 9.13196 11.5004C9.02881 11.1592 8.94946 11.0322 8.75904 11.0322C8.5686 11.0322 8.48926 11.1592 8.37024 11.5004C8.23535 11.9685 7.73547 13.9204 7.60059 14.4916C7.52124 14.7853 7.37049 14.9043 7.14831 14.9043C6.8468 14.9043 6.66431 14.6345 6.74365 14.333C6.76746 14.214 7.4657 11.3893 7.55297 10.8021C7.64026 10.215 7.64026 8.9137 7.64026 8.50903C7.64026 8.23925 7.51331 8.05677 7.29114 8.00915C7.06897 7.96948 5.29162 7.77112 5.03772 7.69178C4.83936 7.63623 4.7124 7.46167 4.7124 7.24744C4.7124 6.96973 4.9187 6.8031 5.10914 6.8031C5.18054 6.8031 5.25195 6.8269 5.32336 6.83484C6.07715 6.95385 7.64026 7.13635 8.75904 7.13635ZM15.805 18.0068C13.5673 18.0068 11.7186 16.1659 11.7186 13.9204C11.7186 11.6829 13.5673 9.84204 15.805 9.84204C18.0425 9.84204 19.8833 11.6829 19.8833 13.9204C19.8833 16.15 18.0187 18.0068 15.805 18.0068ZM13.9721 15.7454C14.2101 15.9834 14.5751 15.9914 14.8052 15.7691L16.2016 14.3648L16.8285 13.6824L16.7571 14.0791L16.7491 15.3406C16.7491 15.6343 16.9872 15.8644 17.2728 15.8644C17.5663 15.8644 17.7965 15.6343 17.7965 15.3406V12.5557C17.7965 12.1272 17.5506 11.9368 17.1776 11.9368H14.4005C14.107 11.9368 13.8768 12.1669 13.8768 12.4525C13.8768 12.7461 14.107 12.9762 14.4005 12.9762H15.6701L16.0508 12.8969L15.3526 13.5316L13.9641 14.9201C13.734 15.1503 13.742 15.5153 13.9721 15.7454Z" />
    </g>
    <defs>
      <clipPath id="clip0_4317_112565">
        <rect
          width="20"
          height="17.5"
          fill="white"
          transform="translate(0 1.5)"
        />
      </clipPath>
    </defs>
  </svg>
);
